import { mdiMagnify, mdiSwapVertical } from '@mdi/js';
import Icon from '@mdi/react';
import {
    Box,
    Button,
    ButtonProps,
    ClickAwayListener,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    SxProps,
    TextField,
} from '@mui/material';
import React, { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useBreakpoints from '../../../hooks/useBreakpoint';
import {
    VulnerabilityDisplayType,
    VulnerabilityViewType,
    setActiveCategory,
    setFilter,
    setFilterVisible,
    setOpenNodes,
    setSortAz,
    setViewType,
} from '../../../redux/vulnerabilities/sliceVulnerabilities';
import { IAppReduxStore } from '../../../redux/store';
import { ceraProBold, vars } from '../../../Theme';
import ViewControls from '../ViewControls';

/** Base button styles. */
const btnSx: SxProps = {
    textTransform: 'none',
    color: vars.font,
    ...ceraProBold,
    p: 1,
};

/** Styles for an active button. */
const btnSxActive: SxProps = {
    color: vars.brandGreen,

    '&:after': {
        backgroundColor: vars.brandGreen,
        position: 'absolute',
        borderRadius: 1,
        content: '""',
        height: 2,
        bottom: 4,
        right: 4,
        left: 4,
    },
};

/**
 * Creates an Icon component.
 * @param path - Icon SVG path.
 */
const icon = (path: string) => <Icon path={path} size={1} />;

/**
 * Gets the translation key for the controls' sorting button.
 * @param sortAz - Sorting direction.
 */
const getSortKey = (sortAz: boolean) => (sortAz ? 'sortAZ' : 'sortZA');

/**
 * Gets the translation key for the controls' expand/collapse button.
 * @param allNodesOpen - Flag that controls if all nodes are open or not.
 */
const getExpandKey = (allNodesOpen: boolean) => (allNodesOpen ? 'collapseAll' : 'expandAll');

/**
 * Component that allows the user to change how their vulnerabilities are displayed.
 */
export const VulnerabilityViewControls = () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'vulnerabilitySelectionView' });

    /** Determine screen size. */
    const { isDesktop } = useBreakpoints();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Get relevant values from Redux store. */
    const filter = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.filter,
    );
    const displayType = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.displayType,
    );
    const viewType = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.viewType,
    );
    const sortAz = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.sortAz,
    );
    const isSearchVisible = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.filterVisible,
    );
    const openNodes = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.settings.openNodes,
    );
    const vulnerabilities = useSelector((store: IAppReduxStore) => store.vulnerabilities.data);

    /** Determine if all nodes are already open. */
    const allNodesOpen = openNodes.length === vulnerabilities.length;

    /**
     * Updates the active vulnerability view type.
     * @param type - New vulnerability view type.
     */
    const updateViewType = (type: VulnerabilityViewType) => dispatch(setViewType(type));

    /**
     * Toggles the vulnerability sorting direction.
     */
    const toggleSort = () => dispatch(setSortAz(!sortAz));

    /**
     * Toggles the visibility of the filter input.
     * @param visible - New input visibility.
     */
    const toggleFilter = (visible: boolean) => dispatch(setFilterVisible(visible));

    /**
     * Gets the filter controls for the view's mobile version.
     */
    const getMobileFilter = () =>
        isSearchVisible || !!filter?.length ? (
            <ClickAwayListener onClickAway={() => toggleFilter(false)}>
                {filterVulnerabilitiesField}
            </ClickAwayListener>
        ) : (
            <IconButton title={t('vulnerabilitySearch')} onClick={() => toggleFilter(true)}>
                {icon(mdiMagnify)}
            </IconButton>
        );

    /** Text input for filtering visible vulnerabilities. */
    const filterVulnerabilitiesField = (
        <TextField
            id='input-filter'
            value={filter}
            onBlur={() => dispatch(setFilterVisible(false))}
            onChange={e => dispatch(setFilter(e.target.value))}
            placeholder={t('vulnerabilitySearch')}
            disabled={!vulnerabilities?.length}
            sx={{ input: { py: 1 } }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position='start'>{icon(mdiMagnify)}</InputAdornment>
                ),
            }}
        />
    );

    /** Maps VulnerabilityViewTypes to an array. */
    const viewTypeMembers: VulnerabilityViewType[] = Object.keys(VulnerabilityViewType).map(
        key => VulnerabilityViewType[key],
    );

    /** Filters the viewTypeMembers array to only include numbers. */
    const viewTypeIndexArr: number[] = viewTypeMembers.filter(
        viewTypeItem => typeof viewTypeItem === 'number',
    );

    /** Maps the viewTypeIndexArr to menu items for the mobile view. */
    const viewTypeMenuItems: JSX.Element[] = viewTypeIndexArr.map(viewTypeIndex => (
        <MenuItem value={viewTypeIndex} key={viewTypeIndex}>
            {t(`vulnerability_${VulnerabilityViewType[viewTypeIndex].toString().toLowerCase()}`)}
        </MenuItem>
    ));

    /** Tablet/Mobile View. */
    if (!isDesktop) {
        return (
            <Stack direction='row' alignItems='center' sx={{ px: 3 }}>
                <Select
                    variant='standard'
                    value={viewType}
                    onChange={e => updateViewType(e.target.value as VulnerabilityViewType)}
                    disabled={!vulnerabilities?.length}
                >
                    {viewTypeMenuItems}
                </Select>

                <Box>
                    <IconButton
                        title={t(getSortKey(sortAz))}
                        onClick={toggleSort}
                        disabled={!vulnerabilities?.length}
                    >
                        {icon(mdiSwapVertical)}
                    </IconButton>

                    {getMobileFilter()}
                </Box>
            </Stack>
        );
    }

    /**
     * Gets the props for a button that changes the active view type.
     * @param viewTypeIndex - View type index to create the button for.
     */
    const viewBtnProps = (viewTypeIndex: VulnerabilityViewType): ButtonProps => {
        const title: string = t(
            `vulnerability_${VulnerabilityViewType[viewTypeIndex].toString().toLowerCase()}`,
        );

        return {
            onClick: () => {
                updateViewType(viewTypeIndex);
                dispatch(setActiveCategory('All'));
            },
            sx: {
                ...btnSx,
                ...(viewType === viewTypeIndex && vulnerabilities?.length ? btnSxActive : {}),
            },
            children: title,
        };
    };

    /** Mapped view type selection buttons for the sub header in the desktop view. */
    const viewTypeBtns: JSX.Element[] = viewTypeIndexArr.map(viewTypeIndex => {
        return (
            <Button
                key={viewTypeIndex}
                {...viewBtnProps(viewTypeIndex)}
                disabled={!vulnerabilities?.length}
            />
        );
    });

    return (
        <ViewControls
            header={<Stack sx={{ ml: 1 }}>{t('vulnerabilities')}</Stack>}
            row1={<>{filterVulnerabilitiesField}</>}
            row2={
                <Stack direction='row' alignItems='center' sx={{ p: 1 }}>
                    <Stack sx={{ flexGrow: 4 }} direction='row' spacing={2}>
                        {viewTypeBtns}
                    </Stack>

                    <Stack direction='row' alignItems='center' flexGrow={0}>
                        {displayType === VulnerabilityDisplayType.Tree && (
                            <Button
                                sx={{ ...btnSx }}
                                onClick={() =>
                                    dispatch(
                                        setOpenNodes(
                                            allNodesOpen
                                                ? []
                                                : vulnerabilities.map(x => x.ticketId),
                                        ),
                                    )
                                }
                            >
                                {t(getExpandKey(allNodesOpen))}
                            </Button>
                        )}

                        <Button
                            sx={{ ...btnSx }}
                            onClick={toggleSort}
                            disabled={!vulnerabilities?.length}
                        >
                            {icon(mdiSwapVertical)}
                            {t(sortAz ? 'sortAZ' : 'sortZA')}
                        </Button>
                    </Stack>
                </Stack>
            }
        />
    );
};
